import React, { useEffect, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { Outlet, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import {
  clearClientPortalSession,
  portalBootstrap,
  portalMe,
} from "../../api/clientPortal";
import ClientPortalUnavailablePage from "./ClientPortalUnavailablePage";
import {
  getClientPortalErrorMessage,
  isPortalUnavailableError,
} from "./clientPortalErrorMessage";

export default function ClientPortalLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const company = params.get("company") || "";
  const q = company ? `?company=${encodeURIComponent(company)}` : "";

  const [brand, setBrand] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState("");
  const ticketDetailMatch = useMatch("/portal-cliente/app/:id");
  const isTicketChat =
    Boolean(ticketDetailMatch) && ticketDetailMatch.params?.id !== "new";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (company) {
          const b = await portalBootstrap(company);
          if (!cancelled) setBrand(b);
        }
        const me = await portalMe();
        if (!cancelled) setUser(me);
      } catch (err) {
        if (!cancelled) {
          if (isPortalUnavailableError(err)) {
            clearClientPortalSession();
            setUnavailable(true);
            setUnavailableMessage(
              getClientPortalErrorMessage(err, {
                context: "bootstrap",
                fallback:
                  "O portal ainda não está disponível para sua empresa. Peça ao suporte da empresa para habilitar o acesso.",
              })
            );
            return;
          }
          clearClientPortalSession();
          navigate(`/portal-cliente${q}`, { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, navigate, q]);

  const logout = () => {
    clearClientPortalSession();
    navigate(`/portal-cliente${q}`);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (unavailable) {
    return (
      <ClientPortalUnavailablePage
        message={unavailableMessage}
        companyName={brand?.name}
      />
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "100dvh", md: "100vh" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: (t) =>
          t.palette.mode === "dark" ? "#0b0b0f" : alpha(t.palette.primary.main, 0.04),
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          flexShrink: 0,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {brand?.logo ? (
            <Box
              component="img"
              src={brand.logo}
              alt=""
              sx={{ height: 32, mr: 2, flexShrink: 0 }}
            />
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {brand?.name || "Portal do Cliente"}
            </Typography>
            {user?.nome ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.nome || user.razaosocial}
              </Typography>
            ) : null}
          </Box>
          <Tooltip title="Sair">
            <IconButton onClick={logout} aria-label="Sair da conta">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: isTicketChat ? "hidden" : "auto",
          display: isTicketChat ? "flex" : "block",
          flexDirection: "column",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Container
          maxWidth={isTicketChat ? false : "md"}
          disableGutters={isTicketChat}
          sx={{
            py: isTicketChat ? 0 : 3,
            flex: isTicketChat ? 1 : undefined,
            display: isTicketChat ? "flex" : "block",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Outlet context={{ company, q, user, brand }} />
        </Container>
      </Box>
    </Box>
  );
}
